import * as yup from 'yup';
import { ICompanyDataModel } from '../data-models/company.data-model';
import { RendererType } from '../data-models/field-definition';
import { BulkDownloadTearsheetZipRenderer } from '../pages/AdminPanel/ManageAccess/BulkTearsheetGeneration/BulkDownloadTearsheetZipRenderer';
import { plainDateField } from './common-schema-defs';
import { FundDataModel } from './Fund.schema';

export const TearsheetStatus = {
  Pending: 'Pending',
  Success: 'Success',
  Error: 'Failed',
} as const;
export type TearsheetStatus = (typeof TearsheetStatus)[keyof typeof TearsheetStatus];

export function bulkTearsheetGenerationFields(
  portfolioCompanies: ICompanyDataModel[],
  funds: FundDataModel[]
) {
  const companyOptions = portfolioCompanies.map((company) => ({
    displayName: company.name,
    value: company.id,
  }));

  const fundOptions = funds.map((fund) => ({
    displayName: fund.name,
    value: fund.id,
  }));

  return {
    companyIds: yup
      .array()
      .of(yup.number().required())
      .required()
      .min(1, 'At least one company must be selected')
      .label('Companies')
      .formMeta({
        renderer: RendererType.multiSelect,
        rendererMeta: {
          values: companyOptions,
        },
      })
      .default([]),
    fundIds: yup
      .array()
      .of(yup.number().required())
      .nullable()
      .label('Funds')
      .formMeta({
        renderer: RendererType.multiSelect,
        rendererMeta: {
          values: fundOptions,
        },
      }),
    asOfDate: plainDateField().required().label('Date'),
  };
}

export function bulkTearsheetGenerationSchema(
  portfolioCompanies: ICompanyDataModel[],
  funds: FundDataModel[]
) {
  return yup.object(bulkTearsheetGenerationFields(portfolioCompanies, funds));
}

export type BulkTearsheetGenerationPayload = yup.InferType<ReturnType<typeof bulkTearsheetGenerationSchema>>;

export function tearSheetsRecordSchema() {
  return yup.object({
    uploadId: yup.number().required().label('Upload ID'),
    status: yup
      .mixed<TearsheetStatus>()
      .oneOf(Object.values(TearsheetStatus))
      .required()
      .default(TearsheetStatus.Pending)
      .label('Status'),
    companyId: yup.number().required().label('Company').meta({
      renderer: RendererType.companyId,
    }),
    fundId: yup.number().required().label('Fund').meta({
      formatter: 'fund',
    }),
    fundName: yup.string().nullable().default(null).label('Fund'),
    asOfDate: yup.date().required().label('Date'),
    url: yup.string().nullable().default(null).label('URL'),
    errorMessage: yup.string().nullable().default(null).label('Error Message'),
  });
}

export type TearSheetsRecord = yup.InferType<ReturnType<typeof tearSheetsRecordSchema>>;

export function tearSheetsUploadSchema() {
  return yup.object({
    id: yup.number().required().label('').gridMeta({
      cellRenderer: BulkDownloadTearsheetZipRenderer,
    }),
    createdAt: yup.date().nullable().label('Generated At').meta({
      formatter: 'dateTime',
    }),
    createdBy: yup.string().nullable().label('Generated By'),
    status: yup
      .mixed<TearsheetStatus>()
      .oneOf(Object.values(TearsheetStatus))
      .required()
      .default(TearsheetStatus.Pending)
      .label('Status'),
    note: yup.string().nullable().default(null).label('Note'),
  });
}

export type TearSheetsUpload = yup.InferType<ReturnType<typeof tearSheetsUploadSchema>>;

export function downloadTearsheetZipResponseSchema() {
  return yup.object({
    url: yup.string().required().label('URL'),
    expiresAt: yup.string().nullable().label('Expires At'),
  });
}
export type DownloadTearsheetZipResponse = yup.InferType<
  ReturnType<typeof downloadTearsheetZipResponseSchema>
>;
